/**
 * Alpine component: facetsAjax
 *
 * AJAX filtering, sorting and "load more" for the Tailwind collection grids
 * (sections/collection-grid-tw, sections/landing-collection-grid). Filter UI
 * lives in snippets/collection-filter-drawer-tw and
 * snippets/collection-filter-pills-tw.
 *
 * - Single source of truth for URL updates: all filter interactions go
 *   through updateFilters(), which rebuilds the URL from the drawer state.
 * - Alpine calls init() automatically, so markup must NOT also use
 *   x-init="init()" (that double-binds the popstate listener).
 */
window.facetsAjax = function facetsAjax() {
  return {
    selectors: ['#Facets', '#Controls', '#ProductGrid', '#FilterPills'],
    loading: false,
    drawerOpen: false,
    loadMoreLoading: false,
    loadMoreNextUrl: null,
    loadMoreHasNext: false,
    priceTimeout: null,
    priceRange: {
      min: 0,
      max: 0,
      absoluteMin: 0,
      absoluteMax: 0,
    },

    init() {
      window.addEventListener('popstate', () => {
        this.swapFrom(location.href, /* push */ false);
      });

      this.syncLoadMoreState();
    },

    open() {
      this.drawerOpen = true;
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => {
        if (this.$refs.drawerPanel) this.$refs.drawerPanel.focus();
      });
    },

    close() {
      this.drawerOpen = false;
      document.body.style.overflow = '';
      this.$nextTick(() => {
        if (this.$refs.filterTrigger) this.$refs.filterTrigger.focus();
      });
    },

    /**
     * Called by the price form (x-init) with the filter's real maximum, in
     * whole dollars. Re-runs after every AJAX swap because the form is
     * replaced, so the bounds follow the current filtered result set.
     */
    setPriceBounds(max) {
      const absoluteMax = Math.max(1, Math.ceil(Number(max) || 0));
      const params = new URLSearchParams(window.location.search);
      const minParam = parseInt(params.get('filter.v.price.gte'), 10);
      const maxParam = parseInt(params.get('filter.v.price.lte'), 10);

      this.priceRange.absoluteMax = Math.max(absoluteMax, Number.isNaN(maxParam) ? 0 : maxParam);
      this.priceRange.min = Number.isNaN(minParam) ? 0 : minParam;
      this.priceRange.max = Number.isNaN(maxParam) ? this.priceRange.absoluteMax : maxParam;

      if (this.priceRange.min > this.priceRange.max) {
        this.priceRange.min = this.priceRange.max;
      }
    },

    /** The collection's base URL (landing grid sets data-collection-path). */
    collectionPath() {
      return (this.$root && this.$root.dataset.collectionPath) || '';
    },

    buildUrlFromCurrentState(pathname = window.location.pathname) {
      const url = new URL(pathname, window.location.origin);
      const current = new URL(window.location.href);

      ['sort_by', 'view'].forEach((key) => {
        const value = current.searchParams.get(key);
        if (value) url.searchParams.set(key, value);
      });

      document.querySelectorAll('#Facets input[type="checkbox"]:checked').forEach((checkbox) => {
        url.searchParams.append(checkbox.name, checkbox.value);
      });

      if (this.priceRange.absoluteMax > 0) {
        if (this.priceRange.min > this.priceRange.absoluteMin) {
          url.searchParams.set('filter.v.price.gte', Math.round(this.priceRange.min));
        }
        if (this.priceRange.max < this.priceRange.absoluteMax) {
          url.searchParams.set('filter.v.price.lte', Math.round(this.priceRange.max));
        }
      }

      return url.toString();
    },

    debouncedUpdate() {
      clearTimeout(this.priceTimeout);
      this.priceTimeout = setTimeout(() => this.updateFilters(), 300);
    },

    updateFilters() {
      this.swapFrom(this.buildUrlFromCurrentState(), true);
    },

    removeFilter(paramName, value) {
      document.querySelectorAll('#Facets input[type="checkbox"]').forEach((checkbox) => {
        if (checkbox.name === paramName && checkbox.value === value) checkbox.checked = false;
      });
      this.updateFilters();
    },

    clearPriceFilter() {
      this.priceRange.min = this.priceRange.absoluteMin;
      this.priceRange.max = this.priceRange.absoluteMax;
      this.updateFilters();
    },

    clearAll() {
      document.querySelectorAll('#Facets input[type="checkbox"]').forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.priceRange.min = this.priceRange.absoluteMin;
      this.priceRange.max = this.priceRange.absoluteMax;
      // On a tag URL (/collections/<handle>/<tag>), clearing also drops the tag.
      this.swapFrom(this.buildUrlFromCurrentState(this.collectionPath() || window.location.pathname), true);
      if (this.drawerOpen) this.close();
    },

    /** Remove an active tag (tag URL) but keep any other filters. */
    removeTag() {
      // /collections/<handle>/<tag> -> /collections/<handle>
      const base = this.collectionPath() || window.location.pathname.replace(/(\/collections\/[^/]+)\/.*$/, '$1');
      this.swapFrom(this.buildUrlFromCurrentState(base), true);
    },

    /** Copy the new product count into the persistent live region, if present. */
    announceResults() {
      const live = document.querySelector('[data-results-announcer]');
      const count = document.querySelector('[data-results-count]');
      if (live && count) live.textContent = count.textContent.replace(/\s+/g, ' ').trim();
    },

    syncLoadMoreState(sourceGrid = null) {
      const targetGrid = document.querySelector('#ProductGrid');
      if (!targetGrid) return;

      if (sourceGrid) {
        ['nextUrl', 'currentPage', 'totalPages'].forEach((key) => {
          if (sourceGrid.dataset[key]) {
            targetGrid.dataset[key] = sourceGrid.dataset[key];
          } else {
            delete targetGrid.dataset[key];
          }
        });
        targetGrid.dataset.hasNext = sourceGrid.dataset.hasNext || 'false';
      }

      this.loadMoreNextUrl = targetGrid.dataset.nextUrl || null;
      this.loadMoreHasNext = targetGrid.dataset.hasNext === 'true';
    },

    setLoading(on) {
      this.loading = !!on;
      document.documentElement.classList.toggle('ajax-loading', this.loading);
    },

    /**
     * Carry the current page's preview_theme_id and alternate template (view)
     * onto an AJAX URL. Without `view`, a page previewed with ?view=landing
     * would fetch the default collection template's markup (and the section
     * rendering request for Load More would miss the section entirely).
     */
    withContext(urlString) {
      const current = new URL(location.href);
      const target = new URL(urlString, location.origin);
      ['preview_theme_id', 'view'].forEach((key) => {
        const value = current.searchParams.get(key);
        if (value && !target.searchParams.get(key)) target.searchParams.set(key, value);
      });
      return target.toString();
    },

    /**
     * Find a selector in a fetched document, including inside <template>
     * content. The filter drawer is wrapped in <template x-teleport>, which
     * querySelector does not search, so #Facets was never refreshed before.
     */
    findInDoc(doc, sel) {
      const direct = doc.querySelector(sel);
      if (direct) return direct;
      for (const tpl of doc.querySelectorAll('template')) {
        const match = tpl.content.querySelector(sel);
        if (match) return match;
      }
      return null;
    },

    async swapFrom(url, push = true) {
      const finalUrl = this.withContext(url);
      this.setLoading(true);
      try {
        const res = await fetch(finalUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error('HTTP ' + res.status);

        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        // Update the URL first so re-rendered markup (e.g. tab state) reads it.
        if (push) history.pushState({ ajax: true }, '', finalUrl);

        // Remember which filter control had focus; its node is replaced below.
        const focused = document.activeElement;
        const focusKey =
          focused && focused.closest('#Facets') && focused.name
            ? `[name="${CSS.escape(focused.name)}"]` + (focused.value ? `[value="${CSS.escape(focused.value)}"]` : '')
            : null;

        for (const sel of this.selectors) {
          const src = this.findInDoc(doc, sel);
          const dst = document.querySelector(sel);
          if (!src || !dst) continue;
          // Copy classes too: visibility (e.g. #FilterPills tw-hidden) lives on the wrapper.
          dst.className = src.className;
          dst.innerHTML = src.innerHTML;
          if (sel === '#ProductGrid') this.syncLoadMoreState(src);
        }

        if (!this.findInDoc(doc, '#ProductGrid')) this.syncLoadMoreState();
        this.announceResults();

        // While the filter dialog is open, keep focus inside it. Otherwise
        // bring the results into view and move focus to the first result.
        if (this.drawerOpen) {
          const restore = focusKey && document.querySelector(`#Facets ${focusKey}`);
          (restore || this.$refs.drawerPanel)?.focus();
        } else {
          const anchor = document.querySelector('#Controls') || document.querySelector('#ProductGrid');
          if (anchor && anchor.getBoundingClientRect().top < 0) {
            anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          const first = document.querySelector('#ProductGrid a, #ProductGrid button');
          if (first) first.focus({ preventScroll: true });
        }
      } catch (err) {
        console.error('[facetsAjax] error:', err);
        location.href = this.withContext(url);
      } finally {
        this.setLoading(false);
      }
    },

    /**
     * True when a quick-filter tab's URL matches the current page: same path
     * and the same filter.* params (sort, view and page are ignored).
     */
    isTabActive(href) {
      const filterParams = (url) =>
        Array.from(url.searchParams.entries())
          .filter(([key]) => key.startsWith('filter.'))
          .map(([key, value]) => `${key}=${value}`)
          .sort()
          .join('&');
      const tab = new URL(href, location.origin);
      const here = new URL(location.href);
      return tab.pathname === here.pathname && filterParams(tab) === filterParams(here);
    },

    /**
     * Links within the current collection (the collection URL itself, or one
     * of its tag URLs, /collections/<handle>/<tag>) load via AJAX; links to
     * other pages navigate normally. The grid sets data-collection-path; if
     * it's missing, only same-path links count.
     */
    isSameCollection(pathname) {
      const base = (this.$root && this.$root.dataset.collectionPath) || '';
      if (!base) return pathname === location.pathname;
      return pathname === base || pathname.startsWith(base + '/');
    },

    onClick(e) {
      const a = e.target.closest('a[data-ajax]');
      if (!a || a.origin !== location.origin || !this.isSameCollection(a.pathname)) return;
      e.preventDefault();
      this.swapFrom(a.href, true);
    },

    onSubmit(e) {
      if (!e.target.closest('form[data-ajax]')) return;
      e.preventDefault();
      this.updateFilters();
    },

    async loadMore() {
      if (this.loadMoreLoading || !this.loadMoreHasNext || !this.loadMoreNextUrl) return;

      const grid = document.querySelector('#ProductGrid');
      const cardsWrapper = grid ? grid.querySelector('[data-grid-items]') || grid.querySelector('.tw-grid') : null;
      if (!grid || !cardsWrapper) return;

      this.loadMoreLoading = true;
      try {
        const sectionId = grid.dataset.sectionId;
        const nextUrl = new URL(this.loadMoreNextUrl, window.location.origin);
        if (sectionId) nextUrl.searchParams.set('section_id', sectionId);

        const res = await fetch(this.withContext(nextUrl.toString()), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);

        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const sourceGrid = doc.querySelector('#ProductGrid');
        if (!sourceGrid) throw new Error('Missing #ProductGrid in response');

        const newItems = sourceGrid.querySelector('[data-grid-items]') || sourceGrid.querySelector('.tw-grid');
        if (newItems) {
          Array.from(newItems.children).forEach((node) => cardsWrapper.appendChild(node));
        }

        this.syncLoadMoreState(sourceGrid);
        document.dispatchEvent(new CustomEvent('collection:load-more', { detail: { sectionId } }));
      } catch (err) {
        console.error('[facetsAjax] load more error:', err);
        location.href = this.withContext(this.loadMoreNextUrl);
      } finally {
        this.loadMoreLoading = false;
      }
    },

    onCheckboxChange() {
      this.updateFilters();
    },

    onSortChange(e) {
      const url = new URL(this.buildUrlFromCurrentState());
      url.searchParams.set('sort_by', e.target.value);
      this.swapFrom(url.toString(), true);
    },
  };
};
